import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ApiHttpService } from './api-http.service';
import { CustomHttpResponse } from '../models/data/CustomHttpResponse';
import { User } from '../models/entities/User/User';
import { UserSettings } from '../models/entities/User/UserSettings';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  #cookieService = inject(CookieService);
  #apiService = inject(ApiHttpService);

  getUserMe(): Observable<User> {
    return this.#apiService.get('/users/me').pipe(
      map((result: CustomHttpResponse<User>) => result.body as User),
      tap((user) => this.#setMercureCookie(user)),
      map((user) => user),
    );
  }

  post(user: User): Observable<User> {
    return this.#apiService.post('/users', user).pipe(map((result: CustomHttpResponse<User>) => result.body as User));
  }

  update(iri: string, entity: User): Observable<User> {
    return this.#apiService.patch(iri, entity).pipe(map((result: CustomHttpResponse<User>) => result.body));
  }

  updateUserMeSettings(user: User, userSettings: UserSettings): Observable<UserSettings> {
    return this.#apiService
      .patch(`/users/${user.id}/settings`, userSettings)
      .pipe(map((result: CustomHttpResponse<UserSettings>) => result.body as UserSettings));
  }

  /** The settings of any user, by the user's iri (e.g. `/users/12`). */
  getSettings(userIri: string): Observable<UserSettings> {
    return this.#apiService
      .get(`${userIri}/settings`)
      .pipe(map((result: CustomHttpResponse<UserSettings>) => result.body as UserSettings));
  }

  /** Saves (part of) the settings of any user, by the user's iri. */
  updateSettings(userIri: string, userSettings: Partial<UserSettings>): Observable<UserSettings> {
    return this.#apiService
      .patch(`${userIri}/settings`, userSettings as UserSettings)
      .pipe(map((result: CustomHttpResponse<UserSettings>) => result.body as UserSettings));
  }

  #setMercureCookie(user: User) {
    if (user.mercureSubscribeToken) {
      this.#cookieService.set('mercureAuthorization', user.mercureSubscribeToken.token, {
        expires: new Date(user.mercureSubscribeToken.expiresAt),
        secure: true,
        sameSite: 'Strict',
        path: '/api/.well-known/mercure',
      });
    }
  }
}
